import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users, Download, Mail, ExternalLink, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function LandingWaitlist() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/landing-pages/${id}/waitlist`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/landing-pages/${id}/waitlist/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `waitlist-${data.page.slug}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || html.trim().length < 10) { toast.error("Subject + email body needed"); return; }
    setSending(true);
    try {
      const { data: r } = await api.post(`/landing-pages/${id}/waitlist/broadcast`, { subject, html });
      toast.success(`Sent ${r.sent} emails · ${r.credits_charged} credits used`);
      setBroadcastOpen(false); setSubject(""); setHtml("");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  if (!data) return <div className="text-[#4A4D57]">Loading…</div>;

  return (
    <div className="space-y-6 vp-fade-up max-w-4xl">
      <div>
        <Link to={`/app/pages/${id}/edit`} className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to editor
        </Link>
        <div className="flex items-start justify-between gap-3 mt-3 flex-wrap">
          <div>
            <div className="vp-label">Waitlist</div>
            <h1 className="font-display text-3xl font-bold mt-1">{data.page.title}</h1>
            <p className="text-[#4A4D57] mt-2 text-sm">Everyone who signed up on your published landing page.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/p/${data.page.slug}`} target="_blank" rel="noreferrer" className="vp-btn-ghost text-sm inline-flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> Preview page
            </a>
            <button onClick={() => setBroadcastOpen(true)} disabled={data.total === 0}
              className="vp-btn-ghost text-sm inline-flex items-center gap-2" data-testid="waitlist-broadcast-btn">
              <Send className="w-4 h-4" /> Broadcast
            </button>
            <button onClick={downloadCsv} disabled={data.total === 0} className="vp-btn-primary text-sm inline-flex items-center gap-2" data-testid="waitlist-export">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="vp-kpi flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center">
            <Users className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="vp-label">Total signups</div>
            <div className="font-display text-3xl font-bold mt-0.5">{data.total}</div>
          </div>
        </div>
        <div className="vp-kpi">
          <div className="vp-label">Named entries</div>
          <div className="font-display text-2xl font-bold mt-1">{data.entries.filter((e) => e.name).length}</div>
        </div>
        <div className="vp-kpi">
          <div className="vp-label">Latest</div>
          <div className="font-display text-sm mt-1">
            {data.entries[0] ? new Date(data.entries[0].created_at).toLocaleString("en-IN") : "—"}
          </div>
        </div>
      </div>

      <div className="vp-card overflow-hidden">
        {data.entries.length === 0 ? (
          <div className="p-10 text-center">
            <Mail className="w-10 h-10 text-[#B8BBC5] mx-auto mb-3" strokeWidth={1.25} />
            <div className="font-display font-bold text-lg">No signups yet</div>
            <p className="text-sm text-[#4A4D57] mt-1 max-w-md mx-auto">
              Add a Waitlist section to your landing page, publish, and share the link.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#E4E6EC]">
            {data.entries.map((e) => (
              <div key={e.id} className="p-4 flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[#0B0D17] truncate">{e.email}</div>
                  {(e.name || e.source) && (
                    <div className="text-xs text-[#747680] mt-0.5 truncate">
                      {e.name}{e.name && e.source ? " · " : ""}{e.source && `from ${e.source}`}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[#747680] shrink-0">{new Date(e.created_at).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Broadcast modal */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="bg-white border-[#E4E6EC] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Broadcast to waitlist</DialogTitle>
            <DialogDescription className="text-[#4A4D57]">
              Sending to {data.total} subscriber{data.total === 1 ? "" : "s"} · costs {Math.max(1, Math.ceil(data.total / 20))} credits.
              Real email delivered via Resend.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="vp-label mb-1.5" style={{ fontSize: "0.65rem" }}>Subject line</div>
              <input className="vp-input text-sm" value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Doors open Monday — your ₹500 early-bird discount inside" data-testid="broadcast-subject" />
            </div>
            <div>
              <div className="vp-label mb-1.5" style={{ fontSize: "0.65rem" }}>Email body (HTML allowed)</div>
              <textarea rows={8} className="vp-input text-sm font-mono" value={html} onChange={(e) => setHtml(e.target.value)}
                placeholder="<p>Hey there,</p><p>The moment you've been waiting for is here…</p>"
                data-testid="broadcast-html" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setBroadcastOpen(false)} className="vp-btn-ghost text-sm" disabled={sending}>Cancel</button>
              <button onClick={sendBroadcast} disabled={sending || !subject.trim() || html.trim().length < 10}
                className="vp-btn-primary text-sm inline-flex items-center gap-2" data-testid="broadcast-send">
                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send broadcast"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
